import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import ConfirmationDialog from '../../../core-components/theme/components/DialogBox/ConfirmationDialog';
import { useDeleteLogbookTemplate } from '../hooks/useDeleteLogbook';
import { LogbookTemplatesPage } from '../types/loogBookTypes';
import { toast } from 'react-toastify';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { LOGBOOK_DELETED } from '../../../modules/analytics/events';

export default function DeleteLogbookModal({ handleClose, data, queryKey }: any) {
    const queryClient = useQueryClient();
    const { logbookTemplateId } = data;
    const deleteMutation = useDeleteLogbookTemplate({
        onMutate: async (templateId: string) => {
            await queryClient.cancelQueries({ queryKey });
            const previousData = queryClient.getQueryData<InfiniteData<LogbookTemplatesPage>>(queryKey);
            if (previousData) {
                queryClient.setQueryData<InfiniteData<LogbookTemplatesPage>>(queryKey, {
                    ...previousData,
                    pages: previousData.pages.map((page, index) => ({
                        ...page,
                        logbookTemplates: page.logbookTemplates.filter(
                            template => template.logbookTemplateId !== templateId
                        ),
                        totalCount: index === 0 ? page.totalCount - 1 : page.totalCount,
                    })),
                });
            }
            return { previousData };
        },
        onError: (err: Error, templateId: string, context: any) => {
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
            toast.error(err?.message);
        },
    }, queryKey);

    const handleDeleteTemplate = (templateId: string) => {
        deleteMutation.mutate(templateId);
        trackEvent(LOGBOOK_DELETED, { logbookTemplateId: templateId, logbookName: data?.name || '' });
    };

    const actions = useMemo(
        () => [
            { text: 'Cancel', handler: handleClose, fullWidth: false, color: '#E0E0E0', textColor: '#000' },
            { text: 'Delete', handler: () => handleDeleteTemplate(logbookTemplateId), fullWidth: false, color: '#FF0000', textColor: '#FFF', disabled: deleteMutation.isPending },
        ],
        [handleClose, handleDeleteTemplate, deleteMutation.isPending, logbookTemplateId]
    );

    return (
        <ConfirmationDialog
            open={true}
            actions={actions}
            handleClose={handleClose}
            title="Delete Logbook"
            description="It will be deleted from everywhere it is used, are you sure you want to delete it?"
        />
    );
}